import { Download } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { qs } from '../lib/api';
import { download } from '../lib/download';
import { useToast } from './Toast';

/** "Export" button with Excel / CSV (/ KMZ) for the list the user is looking at, same filters. */
export function ExportMenu({
  entity,
  params,
  kmz,
}: {
  entity: 'assets' | 'routes' | 'customers';
  params: Record<string, string | undefined | null>;
  kmz?: boolean;
}) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  async function run(kind: 'xlsx' | 'csv' | 'kmz') {
    setOpen(false);
    setBusy(true);
    try {
      if (kind === 'kmz') await download(`/api/v1/exports/kmz${qs({ ...params, routes: 1 })}`, 'ftthnms.kmz');
      else
        await download(`/api/v1/exports/table${qs({ ...params, entity, format: kind })}`, `ftthnms.${kind}`);
    } catch (err) {
      toast({ tone: 'danger', text: (err as Error).message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className="fn-btn fn-btn--secondary fn-btn--sm"
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={busy}
        onClick={() => setOpen((o) => !o)}
      >
        {busy ? <span className="fn-spinner" aria-hidden /> : <Download className="i" aria-hidden />} Export
      </button>
      {open && (
        <div
          role="menu"
          className="fn-panel"
          style={{
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 6px)',
            minWidth: 200,
            padding: 6,
            zIndex: 50,
            background: 'var(--bg-surface)',
          }}
        >
          {(
            [
              ['xlsx', 'Excel (.xlsx)'],
              ['csv', 'CSV'],
              ...(kmz ? [['kmz', 'Google Earth (.kmz)']] : []),
            ] as Array<['xlsx' | 'csv' | 'kmz', string]>
          ).map(([k, label]) => (
            <button key={k} type="button" role="menuitem" className="fn-layer" onClick={() => void run(k)}>
              {label}
            </button>
          ))}
          <p className="fn-hint" style={{ padding: '6px 8px 2px' }}>
            Mengikuti filter dan wilayah yang sedang dipilih.
          </p>
        </div>
      )}
    </div>
  );
}
